import { useEffect, useMemo, useState } from 'react';
import { Table, Typography, notification } from 'antd';

import { axiosGet, axiosPost } from '../../services/http.services';
import {
  API_APP_DOWNLOAD,
  API_DELETE_APP_DOWNLOAD,
  API_UPDATE_APP_DOWNLOAD,
} from '../../api/api';
import AppGraph from '../../customs/AppGraph';
import EditableGridTable from '../../customs/EditableGridTable';


const { Title } = Typography;


const normalizeMonthYear = (value) => {
  const rawValue = String(value || '').trim();
  if (!rawValue) {
    return '';
  }

  const match = rawValue.match(/^([A-Za-z]{3})-(\d{2}|\d{4})$/);
  if (!match) {
    return rawValue;
  }

  const month = match[1].slice(0, 1).toUpperCase() + match[1].slice(1).toLowerCase();
  const year = match[2].slice(-2);
  return `${month}-${year}`;
};


const mapDownloadRow = (row = []) => ({
  id: row[0],
  month_year: row[1],
  android: row[2],
  ios: row[3],
  android_rating: row[4],
  ios_rating: row[5],
});


const toGraphRows = (rows = []) =>
  rows.map((row) => [row.month_year, row.android, row.ios, row.android_rating, row.ios_rating]);


const AppDownloadsSection = ({ canEdit = false, isDownloadPdf = false }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadRows = async () => {
    setLoading(true);
    try {
      const response = await axiosGet(API_APP_DOWNLOAD);
      setRows((response?.data || []).map(mapDownloadRow));
    } catch (error) {
      notification.error({
        message: 'Unable to Load App Downloads',
        description: 'App download data could not be loaded.',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
  }, []);

  const totalsRow = useMemo(() => {
    if (!rows.length) {
      return ['Total', 0, 0, 0, 0];
    }

    const totalAndroid = rows.reduce((sum, row) => sum + Number(row.android || 0), 0);
    const totalIos = rows.reduce((sum, row) => sum + Number(row.ios || 0), 0);
    const avgAndroidRating =
      rows.reduce((sum, row) => sum + Number(row.android_rating || 0), 0) / rows.length;
    const avgIosRating =
      rows.reduce((sum, row) => sum + Number(row.ios_rating || 0), 0) / rows.length;

    return [
      'Total',
      totalAndroid.toFixed(2),
      totalIos.toFixed(2),
      avgAndroidRating.toFixed(2),
      avgIosRating.toFixed(2),
    ];
  }, [rows]);

  const columns = [
    {
      key: 'month_year',
      title: 'Month',
      required: true,
      placeholder: 'Jan-26',
      width: 140,
    },
    {
      key: 'android',
      title: 'Android',
      type: 'number',
      required: true,
      min: 0,
      step: 0.01,
      width: 120,
    },
    {
      key: 'ios',
      title: 'IOS',
      type: 'number',
      required: true,
      min: 0,
      step: 0.01,
      width: 120,
    },
    {
      key: 'android_rating',
      title: 'Ratings (A)',
      type: 'number',
      required: true,
      min: 0,
      max: 5,
      step: 0.01,
      width: 140,
    },
    {
      key: 'ios_rating',
      title: 'Ratings (I)',
      type: 'number',
      required: true,
      min: 0,
      max: 5,
      step: 0.01,
      width: 140,
    },
  ];

  const handleSaveRows = async (changedRows) => {
    for (const row of changedRows) {
      await axiosPost(API_UPDATE_APP_DOWNLOAD, {
        ...row,
        month_year: normalizeMonthYear(row.month_year),
      });
    }
    await loadRows();
  };

  const handleDeleteRow = async (row) => {
    if (!row.id) {
      return;
    }
    await axiosPost(API_DELETE_APP_DOWNLOAD, { id: row.id });
    await loadRows();
  };

  return (
    <div style={{ position: 'relative', marginBottom: 50, marginTop: 25 }}>
      <Title level={4} style={{ textAlign: 'center' }}>
        App Downloads
      </Title>

      <div
        style={{
          maxWidth: '100%',
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          marginTop: 30,
          gap: 25,
        }}
      >
        <EditableGridTable
          data={rows}
          columns={columns}
          rowKey="id"
          canEdit={canEdit}
          onSaveRows={handleSaveRows}
          onDeleteRow={handleDeleteRow}
          getNewRow={() => ({
            id: null,
            month_year: '',
            android: 0,
            ios: 0,
            android_rating: 0,
            ios_rating: 0,
          })}
          cloneRow={(row) => ({
            ...row,
            id: null,
            month_year: '',
          })}
          searchKeys={['month_year']}
          searchPlaceholder="Search month"
          note="Month format should stay like Jan-26."
          toolbarPosition="bottom"
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}>
                  <strong>{totalsRow[0]}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="center">
                  <strong>{totalsRow[1]}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="center">
                  <strong>{totalsRow[2]}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="center">
                  <strong>{totalsRow[3]}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="center">
                  <strong>{totalsRow[4]}</strong>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />

        <AppGraph
          showCompDelay={!loading}
          data={toGraphRows(rows)}
          isDownloadPdf={isDownloadPdf}
          totalDownloads={totalsRow}
        />
      </div>
    </div>
  );
};


export default AppDownloadsSection;
